import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  usePreviewDocuSignMutation,
  useLazyPollOfferByIdQuery,
  useOpenDocusignSenderViewMutation,
  offerInitiationApi,
} from '@/store/query/offer-Initiation';
import type { SendEsignPayload } from '@/types/esignature';
import { PREVIEW_FAILURE_REASONS } from '@/constants/esignature';

interface PreviewArgs {
  id: string;
  payload: SendEsignPayload;
}

// ~90s. A send that waits behind a Sidekiq backlog can still be picked up and
// succeed well past the old 40s window, which reported a false timeout on a
// send that was fine (CORE-5422) — the offer shares that queue. Running out is
// recoverable: preview_docusign discards the stale draft, so the next click
// starts a fresh attempt.
export const POLL_MAX_RETRIES = 45;
export const POLL_INTERVAL_MS = 2000;

// Thrown when the backend gave no reason worth showing the recruiter.
export const PREVIEW_GENERIC_ERROR = 'preview_error';

const BARE_ERROR_CODE = /^[a-z][a-z0-9_]*$/;

// The base query hands back `[code]` for a 2xx-with-errors body and `{ errors: [code] }` for a 4xx.
// A mapped code gets recruiter copy, a readable backend sentence (DocuSign's own message on the
// sender view) passes through, and an unmapped bare code would be gibberish in a toast — so it
// stays generic. Every preview refusal used to collapse into the generic toast (ENG-3996/3995).
export const previewFailureReason = (error: unknown): string => {
  const first = Array.isArray(error)
    ? error[0]
    : (error as { errors?: unknown[] } | undefined)?.errors?.[0];
  if (typeof first !== 'string' || !first.trim()) return PREVIEW_GENERIC_ERROR;
  const text = first.trim();
  const mapped = PREVIEW_FAILURE_REASONS[text.split(':')[0].trim()];
  if (mapped) return mapped;
  return BARE_ERROR_CODE.test(text) ? PREVIEW_GENERIC_ERROR : text;
};

// Generic title, backend reason as the description — the shape handleApiError uses for the send path.
export const previewErrorToast = (error: unknown, title: string) => {
  const reason = (error as Error)?.message;
  return {
    title,
    description:
      reason && reason !== PREVIEW_GENERIC_ERROR ? reason : undefined,
    status: 'error' as const,
  };
};

interface DocusignSendError {
  code?: string;
  message?: string;
}

// Matches how OfferStageDetails renders a docusign_send_error, so one payload doesn't read two ways.
const formatSendError = (error?: DocusignSendError) =>
  [error?.code, error?.message].filter(Boolean).join(': ').toLowerCase() ||
  undefined;

type PollOutcome =
  | { kind: 'ready' }
  | { kind: 'reverted'; reason?: string }
  | { kind: 'timeout' }
  | { kind: 'cancelled' };

// Stops early when the offer leaves docusign_preview rather than exhausting the budget. A poll that
// comes back without data is a failed request, not a failed preview — it just spends a retry.
const pollUntilDraftReady = async (
  pollOffer: ReturnType<typeof useLazyPollOfferByIdQuery>[0],
  offerId: string,
  isCancelled: () => boolean,
  maxRetries = POLL_MAX_RETRIES,
  timeout = POLL_INTERVAL_MS
): Promise<PollOutcome> => {
  let retries = 0;
  while (retries < maxRetries) {
    if (isCancelled()) return { kind: 'cancelled' };
    const response = await pollOffer({ id: offerId });
    const offer = response?.data;
    if (offer) {
      if (offer.offer_status !== 'docusign_preview') {
        return {
          kind: 'reverted',
          reason: formatSendError(offer.docusign_send_error),
        };
      }
      if (offer.esign_envelope?.external_id_present) return { kind: 'ready' };
    }
    retries += 1;
    await new Promise((res) => setTimeout(res, timeout));
  }
  return { kind: 'timeout' };
};

// Single-button "Preview on Docusign". Redirect is same-tab, so the popup blocker never sees it and
// no user-activation window is needed — which is what lets the draft be created asynchronously.
export const useDocusignPreviewFlow = () => {
  const [previewDocuSign] = usePreviewDocuSignMutation();
  const [pollOffer] = useLazyPollOfferByIdQuery();
  const [openSenderView] = useOpenDocusignSenderViewMutation();
  const [isRunning, setIsRunning] = useState(false);
  const dispatch = useDispatch();

  // The success path navigates the tab, so a drawer closed mid-poll must neither redirect nor toast.
  // Reset on setup as well as cleanup: a StrictMode double-invoke would otherwise leave the ref true
  // on a mounted hook and silently cancel every run.
  const isUnmountedRef = useRef(false);
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  const runPreview = useCallback(
    async ({ id, payload }: PreviewArgs) => {
      setIsRunning(true);
      try {
        const enqueued = await previewDocuSign({ id, payload });
        if ('error' in enqueued) {
          throw new Error(previewFailureReason(enqueued.error));
        }

        const outcome = await pollUntilDraftReady(
          pollOffer,
          id,
          () => isUnmountedRef.current
        );
        if (isUnmountedRef.current) return;
        if (outcome.kind === 'reverted') {
          throw new Error(outcome.reason || PREVIEW_GENERIC_ERROR);
        }
        if (outcome.kind === 'timeout') throw new Error(PREVIEW_GENERIC_ERROR);

        const res = await openSenderView({ id });
        if (isUnmountedRef.current) return;
        const url = 'data' in res ? res.data?.url : undefined;
        if ('error' in res) throw new Error(previewFailureReason(res.error));
        if (!url) throw new Error(PREVIEW_GENERIC_ERROR);
        window.location.href = url;
      } catch (error) {
        setIsRunning(false);
        // Only the success path navigates away. On every failure the drawer stays mounted on a
        // pre-preview cache, so the offer no longer matches the server — refetch before the caller
        // toasts, or the card can't offer to reopen a draft the worker did finish building.
        dispatch(offerInitiationApi.util.invalidateTags(['GetOfferById']));
        throw error;
      }
    },
    [previewDocuSign, pollOffer, openSenderView, dispatch]
  );

  return { runPreview, isRunning };
};
